<template>
    <TransitionRoot as="div" :show="Object.keys(user ?? {}).length > 0">
        <Dialog as="div" class="fixed z-50 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center h-full w-full pt-4 px-4 pb-20 bg-gray-50 text-center sm:block sm:p-0" v-if="Object.keys(user ?? {}).length > 0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="w-full h-full relative inline-block align-bottom pt-5 pb-4 text-left overflow-y-auto shadow-xl transform transition-all sm:align-middle sm:py-6 ">
                        <div>
                            <div class="relative px-4 -mt-12 py-12 pb-5 border-b border-gray-200 sm:pb-0 sm:px-6 bg-white">
                                <div class="md:flex md:items-center md:justify-between">
                                    <h1 class="text-4xl tracking-widest uppercase font-bold text-gray-700">
                                        {{ user.profile?.name ?? user.email}}
                                    </h1>
                                    <div class="mt-3 flex gap-4 md:mt-0 md:absolute md:top-12 md:right-6">
                                        <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" @click="userDebugInfo">
                                            <BugAntIcon class="w-5 h-5"></BugAntIcon>
                                        </button>
                                        <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" @click.prevent="masqueradeAsUser">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 576 512"><path fill="currentColor" d="M288 64C39.52 64 0 182.1 0 273.5C0 379.5 78.8 448 176 448c27.33 0 51.21-6.516 66.11-36.79l19.93-40.5C268.3 358.6 278.1 352.4 288 352.1c9.9 .3711 19.7 6.501 25.97 18.63l19.93 40.5C348.8 441.5 372.7 448 400 448c97.2 0 176-68.51 176-174.5C576 182.1 536.5 64 288 64zM160 320c-35.35 0-64-28.65-64-64s28.65-64 64-64c35.35 0 64 28.65 64 64S195.3 320 160 320zM416 320c-35.35 0-64-28.65-64-64s28.65-64 64-64c35.35 0 64 28.65 64 64S451.3 320 416 320z"/></svg>
                                        </button>
                                        <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" @click.prevent="closeView">Close</button>
                                    </div>
                                </div>
                                <div class="mt-8">
                                    <div class="sm:hidden" v-if="activeTab">
                                        <label for="current-tab" class="sr-only">Select a tab</label>
                                        <select id="current-tab" name="current-tab" class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                                            <option v-for="(tab, hash) in tabs" :key="hash" :selected="activeTab.name === tab.name">{{ tab.name }}</option>
                                        </select>
                                    </div>
                                    <div class="hidden sm:block" v-if="activeTab">
                                        <nav class="-mb-px flex space-x-8">
                                            <a v-for="(tab, hash) in tabs" :key="hash" href="#" @click.prevent="activeTab = tab" :class="[activeTab.name === tab.name ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm']" :aria-current="activeTab.name === tab.name ? 'page' : undefined">
                                                {{ tab.name }}
                                            </a>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                            <div class="pt-8 pb-20 bg-gray-50" v-if="activeTab">
                                <slot name="tab">
                                    <component :is="activeTab.component" :user="user" @refresh="loadUser" />
                                </slot>
                            </div>
                        </div>

                        <Debug
                            :user="user"
                            :open="debugDialogOpen"
                            @close="debugDialogOpen = false"
                        ></Debug>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {onMounted, ref, shallowRef} from "vue"
import {onBeforeRouteUpdate, useRoute, useRouter} from "vue-router"
import {Dialog, TransitionChild, TransitionRoot} from "@headlessui/vue"
import Profile from "./Partials/Profile"
import Account from "./Partials/Account"
import VerifyEmail from "./Partials/VerifyEmail"
import Activity from "./Partials/Activity"
import Transactions from "./Partials/Transactions"
import Mailing from "./Partials/Mailing"
import Support from "./Partials/Support";
import Debug from "./Partials/Debug.vue";
import {BugAntIcon} from "@heroicons/vue/24/solid";

const tabs = {
    '#profile': { name: 'Profile Data', component: Profile },
    '#account': { name: 'Account', component: Account },
    '#activity': { name: 'Activity', component: Activity },
    '#purchases': { name: 'Subscription & Purchases', component: Transactions },
    '#mail': { name: 'Mailing', component: Mailing },
    '#support': { name: 'Support', component: Support }
}

export default {
    name: "Detail",
    components: {
        VerifyEmail,
        TransitionRoot,
        TransitionChild,
        Dialog,
        Debug,
        BugAntIcon,
    },
    setup() {
        const vueRoute = useRoute()
        const vueRouter = useRouter()

        const user = ref(null)
        const activeTab = shallowRef()
        const referrer = ref()

        const debugDialogOpen = ref(false)

        const loadUser = async (userId) => {
            await axios.get(route('users.show', userId))
                .then((response) => {
                    user.value = response.data.user
                })
        }

        onBeforeRouteUpdate((to, from) => {
            if (to.params.userId !== from.params.userId) {
                user.value = null

                if (!window.location.hash) {
                    activeTab.value = tabs['#profile']
                } else {
                    activeTab.value = tabs[window.location.hash]
                }

                loadUser(to.params.userId)
            }
        })

        onMounted(() => {
            referrer.value = vueRouter.options.history.state.back

            if (!window.location.hash) {
                activeTab.value = tabs['#profile']
            } else {
                activeTab.value = tabs[window.location.hash]
            }

            loadUser(vueRoute.params.userId)
        })

        const closeView = () => {
            if (referrer.value) {
                vueRouter.push(referrer.value)
            } else {
                vueRouter.go(-2)
            }
        }

        const masqueradeAsUser = () => {
            let redirect = window.open(route('users.masquerade', user.value.id), '_blank')
            redirect.location
        }

        const userDebugInfo = () => {
            debugDialogOpen.value = true
        }

        return {
            tabs,
            user,
            loadUser,
            closeView,
            activeTab,
            masqueradeAsUser,
            referrer,
            debugDialogOpen,
            userDebugInfo,
        }
    },
}
</script>
